/**
 * Persists local MP3 blobs in IndexedDB, keyed by track ID.
 * The API only stores a filename hint; the actual file lives here per-device.
 */

const DB_NAME = 'SpeedTrainerLocalFiles';
const DB_VERSION = 1;
const STORE_NAME = 'mp3-files';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveFile(trackId: string, file: File): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.put({ blob: file, name: file.name }, trackId);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

export async function getFileEntry(trackId: string): Promise<{ blob: Blob; name: string } | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(trackId);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

export async function hasFile(trackId: string): Promise<boolean> {
  const entry = await getFileEntry(trackId);
  return entry !== null;
}

export async function removeFile(trackId: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.delete(trackId);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

export async function renameFile(oldTrackId: string, newTrackId: string): Promise<void> {
  if (oldTrackId === newTrackId) return;
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const getReq = store.get(oldTrackId);
    getReq.onsuccess = () => {
      const value = getReq.result;
      if (value === undefined) {
        resolve();
        return;
      }
      const putReq = store.put(value, newTrackId);
      putReq.onerror = () => reject(putReq.error);
      putReq.onsuccess = () => {
        const delReq = store.delete(oldTrackId);
        delReq.onerror = () => reject(delReq.error);
        delReq.onsuccess = () => resolve();
      };
    };
    getReq.onerror = () => reject(getReq.error);
    tx.oncomplete = () => db.close();
  });
}
